import { FC, useState, createContext, useEffect } from 'react';
import { Loader } from 'semantic-ui-react';


const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME ?? 'DataExtractor';
const DEFAULT_ROLES = ['dev'];

// Must be synced with iRPM.
const DEFAULT_RESOURCES = {
    "/irpm*": [
        "any"
    ],
    "bucket": [
        "read",
        "index",
        "request_write",
        "request_remove",
        "read_chunk",
        "validate_file_size",
        "get_archive_info",
        "generate_archive",
        "get_archive_status",
    ],
    "events": [
        "any"
    ],
    "configs": [
        "read",
        "index",
        "request_write",
        "request_remove",
        "missing_sources",
        "get_sqs_config",
    ],
    "requests": [
        "index",
        "cancel",
        "submit"
    ],
    "venueLibs": [
        "read",
        "index",
        "request_write",
        "request_remove"
    ],
   "statistics": [
        "index"
    ],
    "healthMonitor": [
        "index"
    ]
};


export const ROLES = {
    SUPER_ADMIN: `${PROJECT_NAME}_[owner]`,
    ADMIN: 'Admin',
    DEV: 'dev'
};


export interface User {
    id: string;
    name: string[];
    email: string;
    roles: string[];
    resources: Record<string, string[]>; // resource -> action[]
}


export interface AuthContextData {
    jwt?: any
    user?: User;
}


export const AuthContext = createContext<AuthContextData>({});


function requestJTI(location: URL) {
    const {
        REACT_APP_IRPM_AUTH_URL: authUrl,
        REACT_APP_IRPM_UI_NAME: uiName,
        REACT_APP_PROJECT_NAME: projName
    } = process.env;

    location.searchParams.set('jti', `$\{jti}`);
    location.searchParams.set('nounce', `$\{nounce}`);

    const callbackUrl = location.toString();
    const redirectUrl = `${authUrl}#/name/${uiName}/proj/${projName}/callback/${callbackUrl}`;

    if (window.top) {
        window.top.location.href = redirectUrl;
    }
}


async function requestJWT(jti: string, nounce: string) {
    const { REACT_APP_IRPM_API_URL: apiUrl } = process.env;
    const args = JSON.stringify({ jti, nounce });

    const data = await fetch(`${apiUrl}/get-jwt/${args}`, {
        headers: { 'Content-Type': 'application/json' }
    });


    const json = await data.json();
    if (!json) {
        throw new Error("Server didn't return valid json");
    }

    const { d: jwtData } = json;
    if (!jwtData) {
        throw new Error(`failed to parse jwt from ${JSON.stringify(json)}; args : ${args}`);
    }

    const token = jwtData.jwt.split('.')[1];
    const jwt = atob(token.replace(/-/g, '+').replace(/_/g, '/'));

    return JSON.parse(jwt);
}


function parseUser(jwt: any): User {
    const { name, email, trid: id } = jwt.usr;
    const roles = jwt.usr.role[PROJECT_NAME] || null;

    if (!roles) {
        return { id, name, email, roles: DEFAULT_ROLES, resources: DEFAULT_RESOURCES };
    }

    const resources: Record<string, string[]> = {};
    const { allow } = jwt.usr;
    for (const [resource, actions] of Object.entries(allow)) {
        if (typeof resource !== 'string') {
            continue;
        }

        if (typeof actions !== 'object') {
            continue;
        }

        const allowedActions = [];
        for (const [action, isAllowed] of Object.entries(actions as Object)) {
            if (isAllowed) {
                allowedActions.push(action);
            }
        }

        resources[resource.replace(`/${PROJECT_NAME}/`, '')] = allowedActions;
    }

    return { id, name, email, roles, resources };
}


export const Authenticated: FC<{}> = ({ children }) => {
    const [user, setUser] = useState<User>();
    const location = new URL(window?.top?.location.href || "");

    useEffect(() => {
        const jti = location.searchParams.get('jti');
        const nounce = location.searchParams.get('nounce');
        if (jti && nounce) {
            requestJWT(jti, nounce)
                .then(jwt => {
                    setUser(parseUser(jwt));
                })
                .catch(err => {
                    if (!user) {
                        console.log(err);
                        location.searchParams.delete('jti');
                        location.searchParams.delete('nounce');
                        requestJTI(location);
                    }
                });
        }
        else {
            requestJTI(location);
        }
    }, []);

    if (!user) {
        return <Loader />;
    }

    return (
        <AuthContext.Provider value={{ user }}>
            {children}
        </AuthContext.Provider>
    );
};
